import { useState } from "react";
import {
  Paper,
  PasswordInput,
  TextInput,
  Button,
  Title,
  Text,
  Stack,
  Alert,
  Box,
  Group,
  Divider,
  Anchor,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IconAlertTriangle,
  IconLock,
  IconMail,
} from "@tabler/icons-react";
import { useAuth } from "../auth/AuthContext";
import { Brand } from "../branding/Brand";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  const form = useForm({
    initialValues: { email: "", password: "" },
    validate: {
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Invalid email"),
      password: (v) => (v.length > 0 ? null : "Required"),
    },
  });

  async function handleSubmit(values) {
    setSubmitting(true);
    setErrMsg(null);
    try {
      await login(values.email, values.password);
      const to = location.state?.from?.pathname || "/";
      navigate(to, { replace: true });
    } catch (e) {
      setErrMsg(
        e?.response?.data?.error?.message || e?.message || "Login failed"
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        background:
          "linear-gradient(135deg, var(--mantine-color-gray-0) 0%, var(--mantine-color-gray-1) 100%)",
      }}
    >
      {/* ── Left visual panel ──────────────────────────────────────────── */}
      <Box
        visibleFrom="md"
        style={{
          flex: "1 1 55%",
          minWidth: 0,
          position: "relative",
          overflow: "hidden",
          background:
            "radial-gradient(ellipse at top left, rgba(214,32,43,0.18), transparent 55%)," +
            "radial-gradient(ellipse at bottom right, rgba(67,56,202,0.18), transparent 55%)," +
            "linear-gradient(160deg, #0f1124 0%, #1c1c33 55%, #2a1a1f 100%)",
          color: "#f6f7fb",
          padding: "56px 64px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Decorative diagonal grid */}
        <BackgroundDecor />

        <Box style={{ position: "relative", zIndex: 1 }}>
          <Box
            style={{
              background: "rgba(255,255,255,0.04)",
              padding: "20px 24px",
              borderRadius: 18,
              border: "1px solid rgba(255,255,255,0.08)",
              display: "inline-block",
              backdropFilter: "blur(8px)",
            }}
          >
            <Brand variant="full" captionColor="rgba(255,255,255,0.6)" />
          </Box>
        </Box>

        <Stack gap="xl" style={{ position: "relative", zIndex: 1 }}>
          <div>
            <Text
              size="xs"
              fw={600}
              tt="uppercase"
              c="rgba(255,255,255,0.55)"
              style={{ letterSpacing: 3 }}
            >
              Shini Extra × Duvo Middleware
            </Text>
            <Title order={1} mt="xs" c="white" fw={800} lh={1.1}>
              The integration layer
              <br />
              between Shini Platforms and Duvo.ai
            </Title>
          </div>
        </Stack>

        <Group justify="space-between" style={{ position: "relative", zIndex: 1 }}>
          <Text size="xs" c="rgba(255,255,255,0.45)">
            © {new Date().getFullYear()} Shini Group
          </Text>
          <Text size="xs" c="rgba(255,255,255,0.45)">
            Internal use only
          </Text>
        </Group>
      </Box>

      {/* ── Right form panel ───────────────────────────────────────────── */}
      <Box
        style={{
          flex: "1 1 45%",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 24px",
        }}
      >
        <Box style={{ width: "100%", maxWidth: 420 }}>
          {/* Brand for mobile (when the left panel is hidden) */}
          <Box hiddenFrom="md" mb="xl" ta="center">
            <Brand variant="full" />
          </Box>

          <Stack gap="xs" mb="lg">
            <Title order={2} fw={800}>
              Welcome back
            </Title>
            <Text c="dimmed">
              Sign in to the Shini Extra × Duvo Middleware to manage your
              automation workflows.
            </Text>
          </Stack>

          <Paper shadow="sm" p="xl" radius="lg">
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                {errMsg && (
                  <Alert
                    color="red"
                    variant="light"
                    icon={<IconAlertTriangle size={16} />}
                    title="Sign-in failed"
                  >
                    {errMsg}
                  </Alert>
                )}
                <TextInput
                  label="Email"
                  placeholder="you@shinigroup.com"
                  autoComplete="username"
                  size="md"
                  leftSection={<IconMail size={16} />}
                  required
                  {...form.getInputProps("email")}
                />
                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  autoComplete="current-password"
                  size="md"
                  leftSection={<IconLock size={16} />}
                  required
                  {...form.getInputProps("password")}
                />
                <Button
                  type="submit"
                  loading={submitting}
                  fullWidth
                  size="md"
                  mt="xs"
                >
                  Sign in
                </Button>
              </Stack>
            </form>

            <Divider my="lg" />

            <Text size="xs" c="dimmed" ta="center">
              Need access?{" "}
              <Anchor href="mailto:it@shini.ps" underline="hover">
                Contact IT
              </Anchor>
              .
            </Text>
          </Paper>

          <Text ta="center" size="xs" c="dimmed" mt="lg">
            v0.1 · Internal middleware platform
          </Text>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Animated background for the dark login panel.
 * - Three slowly drifting coloured "auroras" (radial gradients) on independent loops.
 * - A faint diagonal grid that pulses very slowly.
 * - A thin shimmering scan line for a bit of life.
 *
 * Animations are paused for users with prefers-reduced-motion.
 */
function BackgroundDecor() {
  return (
    <>
      <style>{`
        @keyframes loginAuroraA {
          0%   { transform: translate3d(-10%, -10%, 0) scale(1); }
          50%  { transform: translate3d(15%,  10%, 0) scale(1.15); }
          100% { transform: translate3d(-10%, -10%, 0) scale(1); }
        }
        @keyframes loginAuroraB {
          0%   { transform: translate3d(20%, 30%, 0)  scale(1.1); }
          50%  { transform: translate3d(-15%, 15%, 0) scale(1); }
          100% { transform: translate3d(20%, 30%, 0)  scale(1.1); }
        }
        @keyframes loginAuroraC {
          0%   { transform: translate3d(0%, 0%, 0)   scale(1); }
          50%  { transform: translate3d(-12%, -8%, 0) scale(1.2); }
          100% { transform: translate3d(0%, 0%, 0)   scale(1); }
        }
        @keyframes loginGridPulse {
          0%, 100% { opacity: 0.10; }
          50%      { opacity: 0.22; }
        }
        @keyframes loginScan {
          0%   { transform: translateY(-30%); opacity: 0; }
          10%  { opacity: 0.35; }
          50%  { opacity: 0.55; }
          90%  { opacity: 0.25; }
          100% { transform: translateY(130%); opacity: 0; }
        }
        .login-decor {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
        }
        .login-orb {
          position: absolute;
          width: 60vmax;
          height: 60vmax;
          border-radius: 50%;
          filter: blur(60px);
          mix-blend-mode: screen;
          will-change: transform;
        }
        .login-orb.a {
          top: -20%;
          left: -15%;
          background: radial-gradient(circle, rgba(214,32,43,0.55) 0%, rgba(214,32,43,0) 60%);
          animation: loginAuroraA 18s ease-in-out infinite;
        }
        .login-orb.b {
          bottom: -25%;
          right: -20%;
          background: radial-gradient(circle, rgba(86,67,232,0.45) 0%, rgba(86,67,232,0) 60%);
          animation: loginAuroraB 22s ease-in-out infinite;
        }
        .login-orb.c {
          top: 30%;
          right: -10%;
          width: 40vmax;
          height: 40vmax;
          background: radial-gradient(circle, rgba(255,80,89,0.30) 0%, rgba(255,80,89,0) 60%);
          animation: loginAuroraC 26s ease-in-out infinite;
        }
        .login-grid-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0.18;
          animation: loginGridPulse 9s ease-in-out infinite;
        }
        .login-scan {
          position: absolute;
          left: 0;
          right: 0;
          height: 180px;
          background: linear-gradient(
            to bottom,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0.05) 40%,
            rgba(255,255,255,0.12) 50%,
            rgba(255,255,255,0.05) 60%,
            rgba(255,255,255,0) 100%
          );
          animation: loginScan 12s linear infinite;
          will-change: transform, opacity;
        }
        @media (prefers-reduced-motion: reduce) {
          .login-orb,
          .login-grid-svg,
          .login-scan {
            animation: none !important;
          }
        }
      `}</style>
      <div className="login-decor" aria-hidden="true">
        <div className="login-orb a" />
        <div className="login-orb b" />
        <div className="login-orb c" />
        <div className="login-scan" />
        <svg
          className="login-grid-svg"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="grid"
              width="36"
              height="36"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(20)"
            >
              <path
                d="M36 0 L0 0 0 36"
                fill="none"
                stroke="rgba(255,255,255,0.35)"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
    </>
  );
}
